import axios, {
  AxiosHeaders,
  isAxiosError,
  type AxiosError,
  type AxiosInstance,
} from 'axios';
import {
  fromFetchError,
  fromHttpError,
  type HttpErrorBody,
} from '@repo/api-client/errors';

export interface CreateAxiosOptions {
  /**
   * Full base URL including the API version prefix, e.g. `/api`. Hooks
   * should call relative paths (`/orgs`, `/invoices/...`).
   */
  baseUrl: string;
  /**
   * Optional request timeout in ms. Defaults to 30s.
   */
  timeoutMs?: number;
}

/**
 * Build the singleton axios instance the entire web app uses.
 *
 * Responsibilities:
 *   1. Stamp every request with `Content-Type: application/json` and
 *      `Accept: application/json`.
 *   2. Forward `Idempotency-Key` from per-call config (set by the
 *      mutation hooks in `@repo/queries`).
 *   3. Translate `AxiosError` -> `AppError` so React Query receives
 *      the typed failure shape and screens can pattern-match.
 *   4. Plug in `supertokens-website`'s axios interceptors so 401
 *      responses trigger the silent token-refresh + replay path. The
 *      browser sends the session cookie automatically — we just need
 *      `withCredentials: true`.
 */
export function createAxios(options: CreateAxiosOptions): AxiosInstance {
  const instance = axios.create({
    baseURL: options.baseUrl,
    timeout: options.timeoutMs ?? 30_000,
    withCredentials: true,
    headers: new AxiosHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
    }),
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error: unknown) => Promise.reject(toAppError(error)),
  );

  // Note: NO `SuperTokens.addAxiosInterceptors(instance)` here.
  // `supertokens-website` already installs a global
  // `XMLHttpRequest` interceptor at module load (it called
  // `addXMLHttpRequestInterceptor()` from inside its own bootstrap),
  // and axios is built on XHR — so the bearer-attach + 401-refresh-
  // and-replay flow is handled transparently. Calling
  // `addAxiosInterceptors` on top of that is what the SDK warns
  // about with "Our axios and XMLHttpRequest interceptors cannot be
  // used at the same time" — and the SDK silently no-ops the axios
  // pair once XHR is already installed, so any auth header you
  // expected from it would never actually be applied.
  //
  // Mobile (`apps/mobile`) is different: React Native ships
  // `XMLHttpRequest` but the SDK's XHR install is gated to web, so
  // the axios variant IS the one that runs there.

  return instance;
}

function toAppError(error: unknown): unknown {
  if (isAxiosError(error)) {
    return fromAxiosError(error);
  }
  return fromFetchError(error);
}

function fromAxiosError(error: AxiosError): unknown {
  if (error.response) {
    const body = unwrapErrorEnvelope(error.response.data);
    return fromHttpError(error.response.status, body, error);
  }
  return fromFetchError(error);
}

function unwrapErrorEnvelope(data: unknown): HttpErrorBody | null {
  if (!data || typeof data !== 'object') return null;
  const root = data as Record<string, unknown>;
  const inner = root.error;
  if (inner && typeof inner === 'object') {
    const env = inner as Record<string, unknown>;
    return {
      message: typeof env.message === 'string' ? env.message : undefined,
      code: typeof env.code === 'string' ? env.code : undefined,
      fields: env.fields as HttpErrorBody['fields'],
      details: env.meta as HttpErrorBody['details'],
    };
  }
  return root as HttpErrorBody;
}
